import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs/Subscription';
import {NotificationManager} from '../../../../../../../common/providers/notification';
import {TranslateManager} from '../../../../../../../common/translate';
import {InjectOptionDialog, OptionDialog} from '../../../../../shared/option-dialog';
import {CustomerProfileApiAccessService} from '../../api/service/customer-profile-api-access.service';
import {CustomerProfileService} from '../../api/service/customer-profile.service';
import {ApiAccessAudit, ApiAccessConsumer, ApiAccessKey} from '../../api/entity/profile-api-access';
import {CUSTOMER_API_ACCESS_TAB_ROUTE} from '../../../../../core/security';

const KEYS = 'CUSTOMER.DETAILS.TAB.API_ACCESS.';
// Same rule as the backend
const NAME_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;

// The permissions eapi checks that the BO manages (eapi.supported_authority ids)
const PERMISSION_OPTIONS: { id: number, key: string }[] = [
    {id: 2, key: KEYS + 'PERMISSION.BALANCES'},
    {id: 3, key: KEYS + 'PERMISSION.TRANSACTION_HISTORY'},
    {id: 4, key: KEYS + 'PERMISSION.EXTERNAL_PAYMENTS'},
    {id: 5, key: KEYS + 'PERMISSION.INTERNAL_PAYMENTS'},
    {id: 6, key: KEYS + 'PERMISSION.MASTER_PAYMENTS'}
];

const ACTION_KEYS: { [action: string]: string } = {
    CREATE: KEYS + 'ACTIVITY.ACTIONS.CREATE',
    ROTATE_KEY: KEYS + 'ACTIVITY.ACTIONS.ROTATE_KEY',
    BLOCK: KEYS + 'ACTIVITY.ACTIONS.BLOCK',
    UNBLOCK: KEYS + 'ACTIVITY.ACTIONS.UNBLOCK',
    UPDATE_PERMISSIONS: KEYS + 'ACTIVITY.ACTIONS.UPDATE_PERMISSIONS'
};

@Component({
    selector: 'tfl-profile-api-access',
    template: require('./profile.api-access.component.html'),
    providers: [CustomerProfileApiAccessService],
    styles: [`
        .api-access-key {
            font-family: monospace;
        }

        .state-active {
            color: green !important;
        }

        .state-blocked {
            color: red !important;
        }

        .state-other {
            color: #f57511 !important;
        }

        .api-access-actions .btn {
            margin: 0 4px 4px 0;
        }
    `]
})
export class ProfileApiAccessComponent implements OnInit, OnDestroy {

    readonly apiAccessTabRoute = CUSTOMER_API_ACCESS_TAB_ROUTE;
    readonly permissionOptions = PERMISSION_OPTIONS;

    consumers: ApiAccessConsumer[] = [];
    loaded = false;
    loadError: string = null;
    // A change is in progress: blocks the buttons until the server answers
    busy = false;

    // Create form
    showCreateForm = false;
    newName = '';
    newAuthorities: { [id: number]: boolean } = {};
    private suggestedName = '';

    // Key shown once after create or rotate; dropped when the admin confirms it is stored
    shownKey: string = null;
    shownKeyName: string = null;
    keyCopied = false;

    // Permissions editor and activity, one consumer at a time
    editingConsumerId: number = null;
    editAuthorities: { [id: number]: boolean } = {};
    activityConsumerId: number = null;
    activity: ApiAccessAudit[] = [];
    activityError: string = null;

    private _profileId: number;
    private _routeSubscription: Subscription;

    @InjectOptionDialog('api-access-dialog') dialog: OptionDialog;

    constructor(private activatedRoute: ActivatedRoute,
                private _apiAccessService: CustomerProfileApiAccessService,
                private _customerProfileService: CustomerProfileService,
                private _notificationManager: NotificationManager,
                private _translateManager: TranslateManager) {
    }

    ngOnInit(): void {
        this._routeSubscription = this.activatedRoute.parent.params.subscribe(params => {
            this._profileId = +params['id'];
            this.dismissKey();
            this.loadConsumers();
            this.loadSuggestedName();
        });
    }

    ngOnDestroy(): void {
        // Never keep the key after leaving the tab
        this.dismissKey();
        if (this._routeSubscription) {
            this._routeSubscription.unsubscribe();
        }
    }

    loadConsumers(): void {
        this.loadError = null;
        this._apiAccessService.getConsumers(this._profileId)
            .subscribe(
                consumers => {
                    this.consumers = consumers || [];
                    this.loaded = true;
                },
                error => {
                    this.loaded = true;
                    this.loadError = this.errorMessage(error);
                }
            );
    }

    // One active API access per customer: creating another one is only offered when there is none
    get hasActiveConsumer(): boolean {
        return this.consumers.some(consumer => consumer.state === 'ACTIVE');
    }

    // ---------------------------------------------------------------- create

    openCreateForm(): void {
        this.newName = this.suggestedName;
        this.newAuthorities = {};
        this.showCreateForm = true;
    }

    closeCreateForm(): void {
        this.showCreateForm = false;
    }

    get newNameValid(): boolean {
        return NAME_PATTERN.test((this.newName || '').trim());
    }

    create(): void {
        const authorities = this.selected(this.newAuthorities);
        if (!this.newNameValid || authorities.length === 0 || this.busy) {
            return;
        }
        this.busy = true;
        this._apiAccessService.create(this._profileId, this.newName.trim(), authorities)
            .subscribe(
                result => {
                    this.busy = false;
                    this.showCreateForm = false;
                    this.showKey(result);
                    this._notificationManager.success(this._translateManager.getValue(KEYS + 'CREATED'), 5000);
                    this.loadConsumers();
                },
                error => {
                    this.busy = false;
                    this._notificationManager.error(this.errorMessage(error));
                }
            );
    }

    // ---------------------------------------------------------------- key shown once

    onKeyCopied(): void {
        this.keyCopied = true;
    }

    dismissKey(): void {
        this.shownKey = null;
        this.shownKeyName = null;
        this.keyCopied = false;
    }

    // ---------------------------------------------------------------- rotate / block / unblock

    rotateKey(consumer: ApiAccessConsumer): void {
        this.confirm(KEYS + 'ROTATE.TITLE', KEYS + 'ROTATE.MESSAGE', consumer, KEYS + 'ROTATE.CONFIRM', 'btn-danger', () =>
            this._apiAccessService.rotateKey(this._profileId, consumer.id)
                .subscribe(
                    result => {
                        this.busy = false;
                        this.showKey(result);
                        this._notificationManager.success(this._translateManager.getValue(KEYS + 'ROTATED'), 5000);
                        this.afterChange(consumer.id);
                    },
                    error => this.onActionError(error)
                ));
    }

    block(consumer: ApiAccessConsumer): void {
        this.confirm(KEYS + 'BLOCK.TITLE', KEYS + 'BLOCK.MESSAGE', consumer, KEYS + 'BLOCK.CONFIRM', 'btn-danger', () =>
            this._apiAccessService.block(this._profileId, consumer.id)
                .subscribe(
                    () => {
                        this.busy = false;
                        this._notificationManager.success(this._translateManager.getValue(KEYS + 'BLOCKED'), 5000);
                        this.afterChange(consumer.id);
                    },
                    error => this.onActionError(error)
                ));
    }

    unblock(consumer: ApiAccessConsumer): void {
        this.confirm(KEYS + 'UNBLOCK.TITLE', KEYS + 'UNBLOCK.MESSAGE', consumer, KEYS + 'UNBLOCK.CONFIRM', 'btn-primary', () =>
            this._apiAccessService.unblock(this._profileId, consumer.id)
                .subscribe(
                    () => {
                        this.busy = false;
                        this._notificationManager.success(this._translateManager.getValue(KEYS + 'UNBLOCKED'), 5000);
                        this.afterChange(consumer.id);
                    },
                    error => this.onActionError(error)
                ));
    }

    // ---------------------------------------------------------------- permissions

    editPermissions(consumer: ApiAccessConsumer): void {
        if (this.editingConsumerId === consumer.id) {
            this.editingConsumerId = null;
            return;
        }
        this.editingConsumerId = consumer.id;
        this.editAuthorities = {};
        (consumer.authorities || []).forEach(id => this.editAuthorities[id] = true);
    }

    savePermissions(consumer: ApiAccessConsumer): void {
        const authorities = this.selected(this.editAuthorities);
        if (authorities.length === 0 || this.busy) {
            return;
        }
        this.busy = true;
        this._apiAccessService.updatePermissions(this._profileId, consumer.id, authorities)
            .subscribe(
                () => {
                    this.busy = false;
                    this.editingConsumerId = null;
                    this._notificationManager.success(this._translateManager.getValue(KEYS + 'PERMISSIONS_UPDATED'), 5000);
                    this.afterChange(consumer.id);
                },
                error => this.onActionError(error)
            );
    }

    hasSelection(selection: { [id: number]: boolean }): boolean {
        return this.selected(selection).length > 0;
    }

    permissionLabels(consumer: ApiAccessConsumer): string[] {
        return this.permissionOptions
            .filter(option => (consumer.authorities || []).indexOf(option.id) >= 0)
            .map(option => option.key);
    }

    // ---------------------------------------------------------------- activity

    toggleActivity(consumer: ApiAccessConsumer): void {
        if (this.activityConsumerId === consumer.id) {
            this.activityConsumerId = null;
            return;
        }
        this.activityConsumerId = consumer.id;
        this.loadActivity(consumer.id);
    }

    actionKey(action: string): string {
        return ACTION_KEYS[action] || action;
    }

    // "field: before -> after" for every field the action changed (the key itself is never stored)
    changes(entry: ApiAccessAudit): string[] {
        const before = entry.before || {};
        const after = entry.after || {};
        const fields = Object.keys(after).concat(Object.keys(before).filter(field => !(field in after)));
        return fields.map(field => {
            const from = this.formatValue(before[field]);
            const to = this.formatValue(after[field]);
            return entry.before ? `${field}: ${from} → ${to}` : `${field}: ${to}`;
        });
    }

    stateClass(state: string): string {
        switch (state) {
            case 'ACTIVE':
                return 'state-active';
            case 'BLOCKED':
                return 'state-blocked';
            default:
                return 'state-other';
        }
    }

    // ---------------------------------------------------------------- helpers

    private loadActivity(consumerId: number): void {
        this.activity = [];
        this.activityError = null;
        this._apiAccessService.getAudit(this._profileId, consumerId)
            .subscribe(
                entries => this.activity = entries || [],
                error => this.activityError = this.errorMessage(error)
            );
    }

    private afterChange(consumerId: number): void {
        this.loadConsumers();
        if (this.activityConsumerId === consumerId) {
            this.loadActivity(consumerId);
        }
    }

    private showKey(result: ApiAccessKey): void {
        this.shownKey = result.apiKey;
        this.shownKeyName = result.consumer ? result.consumer.name : null;
        this.keyCopied = false;
    }

    private confirm(title: string, message: string, consumer: ApiAccessConsumer, confirmText: string,
                    confirmClass: string, action: () => void): void {
        if (this.busy) {
            return;
        }
        this.dialog
            .showConfirmDialog({
                title: title,
                descriptionLines: [{key: message, params: {name: consumer.name}}],
                confirmButtonText: confirmText,
                cancelButtonText: KEYS + 'CANCEL',
                confirmButtonClass: confirmClass
            })
            .onConfirmAction(() => {
                this.busy = true;
                action();
            });
    }

    private onActionError(error: any): void {
        this.busy = false;
        this._notificationManager.error(this.errorMessage(error));
    }

    private selected(selection: { [id: number]: boolean }): number[] {
        return this.permissionOptions
            .filter(option => !!selection[option.id])
            .map(option => option.id);
    }

    private formatValue(value: any): string {
        if (value === null || value === undefined) {
            return '-';
        }
        return Array.isArray(value) ? (value.length ? value.join(', ') : '-') : String(value);
    }

    // Proposed name: <profile-name>-api, in the format the backend accepts
    private loadSuggestedName(): void {
        this.suggestedName = '';
        this._customerProfileService.searchCustomerProfileById(this._profileId)
            .subscribe(
                profile => {
                    const base = ((profile && profile.name) || '').toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-+|-+$/g, '')
                        .substring(0, 56)
                        .replace(/-+$/g, '');
                    const name = base + '-api';
                    this.suggestedName = NAME_PATTERN.test(name) ? name : '';
                },
                () => this.suggestedName = ''
            );
    }

    private errorMessage(error: any): string {
        return (error && error.message) || this._translateManager.getValue(KEYS + 'ACTION_ERROR');
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/modules/customers/shared/components/api-access/profile.api-access.component.ts